import {
  ChangeDetectionStrategy,
  Component,
  computed,
  HostBinding,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import {
  AutomationContext,
  AutomationRuleGroupBoolean,
  automationRuleGroupBooleanOptions,
} from '@kinet-open-network/shared/data-access/model';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { MenuModule } from 'primeng/menu';
import { FieldChangeTriggerRuleContainerComponent } from './field-change-trigger-rule-container.component';
import {
  FieldChangeTriggerService,
  IFieldChangeRuleGroup,
} from './field-change-trigger.service';

@Component({
  selector: 'kon-field-change-trigger-rule-group',
  imports: [
    FieldChangeTriggerRuleContainerComponent,
    ButtonModule,
    MenuModule,
    DividerModule,
  ],
  template: `
    <div class="flex items-center gap-2 pl-2">
      <button
        type="button"
        (click)="toggleCollapsed()"
        class="flex items-center gap-2 grow"
      >
        <i
          [class]="collapsed() ? 'pi pi-chevron-right' : 'pi pi-chevron-down'"
        ></i>
        <span class="font-medium text-sm">Skupina podmienok - Zmena poľa</span>
      </button>

      <div>
        <button
          pButton
          type="button"
          icon="pi pi-ellipsis-v"
          size="small"
          class="p-button-text"
          (click)="menu.toggle($event)"
        ></button>
        <p-menu #menu [popup]="true" [model]="menuItems()" appendTo="body">
        </p-menu>
      </div>
    </div>

    @if (!collapsed()) {
      @for (child of children(); track child.id) {
        @if (child.type === 'rule') {
          <kon-field-change-trigger-rule-container
            [context]="context()"
            [rule]="child"
            (ruleChange)="handleChildChange($index, $event)"
            (ruleRemove)="handleRemoveChild($index)"
          />
        } @else if (child.type === 'group') {
          <kon-field-change-trigger-rule-group
            [context]="context()"
            [group]="child"
            (groupChange)="handleChildChange($index, $event)"
            (removeGroup)="handleRemoveChild($index)"
          />
        }

        @if (!$last) {
          <span class="text-gray-400 text-sm pl-2">{{
            booleanOptions.get(boolean())?.label
          }}</span>
        }
      } @empty {
        <p class="text-gray-400 pl-2">Žiadne podmienky</p>
      }

      <p-divider class="my-1" />

      <div class="flex items-center gap-2">
        <button
          pButton
          type="button"
          icon="pi pi-plus"
          label="Pridať podmienku"
          size="small"
          [outlined]="true"
          (click)="handleAddRule()"
        ></button>

        <button
          pButton
          type="button"
          icon="pi pi-plus"
          label="Pridať skupinu"
          size="small"
          [outlined]="true"
          (click)="handleAddGroup()"
        ></button>
      </div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldChangeTriggerRuleGroupComponent {
  readonly #triggerService = inject(FieldChangeTriggerService);

  readonly context = input<AutomationContext | undefined>();
  readonly group = model<IFieldChangeRuleGroup>(
    this.#triggerService.createRuleGroup(),
  );
  readonly canDelete = input<boolean>(true);
  readonly removeGroup = output<void>();

  readonly collapsed = signal(false);
  readonly children = computed(() => this.group().children);
  readonly boolean = computed(() => this.group().boolean);
  readonly nextBoolean = computed(() => {
    if (this.boolean() === AutomationRuleGroupBoolean.And) {
      return AutomationRuleGroupBoolean.Or;
    }
    return AutomationRuleGroupBoolean.And;
  });

  readonly booleanOptions = automationRuleGroupBooleanOptions;

  readonly menuItems = computed<MenuItem[]>(() => [
    {
      label: `Zmeniť operátor na "${this.booleanOptions.get(this.nextBoolean())?.label.toLowerCase()}"`,
      icon: 'pi pi-refresh',
      command: () => this.handleChangeBoolean(),
    },
    {
      label: this.collapsed() ? 'Rozbaliť skupinu' : 'Zbaliť skupinu',
      icon: this.collapsed() ? 'pi pi-plus' : 'pi pi-minus',
      command: () => this.toggleCollapsed(),
    },
    ...(this.canDelete()
      ? [
          {
            label: 'Zmazať skupinu',
            icon: 'pi pi-trash',
            command: () => this.removeGroup.emit(),
          },
        ]
      : []),
  ]);

  @HostBinding('class')
  readonly hostClass = 'flex flex-col gap-2 p-2 border rounded-lg mb-2';

  toggleCollapsed() {
    this.collapsed.update((c) => !c);
  }

  handleAddRule() {
    const children = this.children();
    const newRule = this.#triggerService.createRule();
    children.push(newRule);
    this.group.set({ ...this.group(), children });
  }

  handleAddGroup() {
    const children = this.children();
    const newGroup = this.#triggerService.createRuleGroup();
    children.push(newGroup);
    this.group.set({ ...this.group(), children });
  }

  handleChildChange(
    index: number,
    child:
      | IFieldChangeRuleGroup
      | {
          id?: string;
          type?: string;
          rule?: string;
          key?: string;
          operator?: string;
          value?: unknown;
        },
  ) {
    const children = this.children();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    children[index] = child as any;
    this.group.set({ ...this.group(), children: [...children] });
  }

  handleRemoveChild(index: number) {
    const children = this.children();
    children.splice(index, 1);
    this.group.set({ ...this.group(), children: [...children] });
  }

  handleChangeBoolean() {
    const boolean = this.nextBoolean();
    this.group.set({ ...this.group(), boolean });
  }
}
